import { Box, Card, CardMedia, Typography, IconButton, Stack } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { OfferSearchResult, SearchFilters } from '../types/search.types';
import { PLACEHOLDER_IMAGES } from '@/shared/constants/placeholders';
import { AppButton } from '@/shared/components';
import { useSnackbar } from '@/shared/hooks/useSnackbar';
import offerDetailService from '../../offer-detail/services/offerDetailService';
import { formatPriceWithAbbr } from '@/shared/utils/formatters';
import { useFavoritesCountContext } from '@/features/favorites/context/FavoritesCountContext';

interface OfferCardProps {
  readonly offer: OfferSearchResult;
  readonly searchFilters?: SearchFilters;
  readonly onFavoriteUpdate?: (offerId: string, isFavorite: boolean) => void;
}

export function OfferCard({ offer, searchFilters, onFavoriteUpdate }: Readonly<OfferCardProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { refreshCount } = useFavoritesCountContext();
  const [isFavorite, setIsFavorite] = useState(offer.isFavorite || false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Get first product and its first photo
  const firstProduct = offer.products[0];
  const firstPhoto = firstProduct?.photos[0];
  const productName = firstProduct?.material?.subcategory || firstProduct?.name || '';

  // Use backend-calculated availability duration (already calculated based on search date or today)
  const availabilityWeeks = offer.availabilityDuration || 0;

  const handleDetailsClick = () => {
    navigate(`/offers/${offer.id}`, {
      state: {
        from: 'list',
        searchFilters,
        offerData: offer, // Pass full offer object with pre-calculated distance/availability
      },
    });
  };

  const handleFavoriteClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // Prevent card click
    try {
      setIsUpdating(true);
      await offerDetailService.toggleFavorite(offer.id, isFavorite);
      const newFavoriteState = !isFavorite;
      setIsFavorite(newFavoriteState);
      onFavoriteUpdate?.(offer.id, newFavoriteState);
      refreshCount();
      showSnackbar(
        newFavoriteState ? t('offerDetail.addToFavorites') : t('offerDetail.removeFromFavorites'),
        { variant: 'success' }
      );
    } catch (error: unknown) {
      const apiError = error as { response?: { status?: number } };
      if (apiError.response?.status === 401) {
        showSnackbar('Please log in to save favorites', { variant: 'warning' });
      } else {
        showSnackbar('Failed to update favorites', { variant: 'error' });
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card
      sx={{
        display: 'flex',
        position: 'relative',
        mb: 2,
        p: 1.75,
        background: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px -1px rgba(0, 0, 0, 0.1)',
        borderRadius: '14px',
      }}
    >
      {/* Product Image */}
      <CardMedia
        component="img"
        sx={{
          width: '96px',
          height: '96px',
          flexShrink: 0,
          objectFit: 'cover',
          backgroundColor: 'grey.100',
          borderRadius: '16px',
        }}
        image={firstPhoto?.thumbnailUrl || firstPhoto?.url || PLACEHOLDER_IMAGES.product}
        alt={offer.title}
      />

      {/* Content Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          ml: 1.5,
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* Line 1: Title + Favorite Icon */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}
        >
          <Typography
            variant="h6"
            data-testid="offer-card-title"
            sx={{
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px',
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              mr: 1,
            }}
          >
            {offer.title}
          </Typography>
          <IconButton
            size="small"
            onClick={handleFavoriteClick}
            disabled={isUpdating}
            sx={{
              width: '30px',
              height: '30px',
              padding: '6px',
              background: 'rgba(255, 255, 255, 0.9)',
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px -1px rgba(0, 0, 0, 0.1)',
              borderRadius: '50%',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.95)',
              },
            }}
            aria-label={
              isFavorite ? t('offerDetail.removeFromFavorites') : t('offerDetail.addToFavorites')
            }
          >
            {isFavorite ? (
              <FavoriteIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
            ) : (
              <FavoriteBorderIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
            )}
          </IconButton>
        </Box>

        {/* Line 2: Product Name Subtitle */}
        <Typography
          variant="body2"
          data-testid="offer-card-product-name"
          sx={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mb: 0.5,
            minHeight: '20px', // Maintain consistent height even if empty
          }}
        >
          {productName}
        </Typography>

        {/* Line 3: Distance */}
        {offer.distance !== undefined && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
            <LocationOnIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: 'primary.main',
              }}
            >
              {offer.distance} km
            </Typography>
          </Stack>
        )}

        {/* Line 4: Availability */}
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: 'text.secondary',
            mb: 0.75,
          }}
        >
          {t('search.results.availability', {
            count: availabilityWeeks,
            weeks: t(availabilityWeeks === 1 ? 'search.results.week' : 'search.results.weeks'),
          })}
        </Typography>

        {/* Line 5: Unit Price */}
        {firstProduct?.unitPrice != null && (
          <Typography
            variant="body2"
            data-testid="offer-card-unit-price"
            sx={{
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              color: 'text.secondary',
              mb: 0.75,
            }}
          >
            {t('search.results.unitPrice', {
              price: formatPriceWithAbbr(firstProduct.unitPrice),
              unit: firstProduct.unit?.labelEn ?? firstProduct.unit?.labelFr ?? '',
            })}
          </Typography>
        )}

        {/* Line 6: Details Button */}
        <AppButton
          variant="primary"
          size="small"
          sx={{ alignSelf: 'flex-end' }}
          onClick={handleDetailsClick}
        >
          Détails
        </AppButton>
      </Box>
    </Card>
  );
}
